import { browser, browserVersion, device, deviceLanguage, os } from 'common/BrowserUtilities';
import { ClientEvents, ClientTrackableEvents } from 'common/events/ClientEvents';
import { ISession, ISessionManager } from 'common/sessions';
import { JSONObj } from 'common/TypeUtilities';
import { extractUtmParams, UtmParams } from 'common/urls/UtmUtils';
import { GqlClient } from 'GqlClient';
import { omitBy } from 'lodash';
import { createTaggedLogger } from 'logging/createTaggedLogger';
import { IngestAnalyticsEventDocument } from 'utils/gqlReactTypings.generated.d';
import { v4 as uuid } from 'uuid';
import { ITracker } from './ITracker';

const logger = createTaggedLogger('server-tracker');

export class ServerTracker implements ITracker {
  private client: GqlClient;

  private browserTrackerId: string;

  private sessionManager: ISessionManager;

  private readonly utmParams: UtmParams;

  constructor({
    client,
    browserTrackerId,
    sessionManager,
  }: {
    client: GqlClient;
    browserTrackerId: string;
    sessionManager: ISessionManager;
  }) {
    this.client = client;
    this.browserTrackerId = browserTrackerId;
    this.sessionManager = sessionManager;
    // Extract UTM params once at initialization to ensure they're available for all events
    // Guard against SSR by checking if window is defined
    this.utmParams = typeof window !== 'undefined' ? extractUtmParams(window.location.search) : {};

    this.initSession();
  }

  public track<K extends keyof ClientTrackableEvents>(eventName: K, data: ClientTrackableEvents[K]): void {
    this.sessionManager.touch();

    const enhancedData = this.enhanceServerTrackingData(data);
    const stringifiedData = JSON.stringify(enhancedData);
    this.client
      .mutate({
        mutation: IngestAnalyticsEventDocument,
        variables: { eventName, data: stringifiedData },
      })
      .then(({ errors }) => {
        if (errors?.length) {
          logger.error('Failed to ingest analytics event', { errors, eventName });
        }
      })
      .catch((error) => {
        logger.error('Failed to ingest analytics event', { error, eventName });
      });
  }

  private initSession() {
    this.sessionManager.addNewSessionListener(this.trackSessionCreation);
    this.sessionManager.getOrCreateSession();
  }

  private trackSessionCreation = (args: ISession) => {
    const { id, startedAt } = args;

    // Use the UTM params that were extracted at initialization
    this.track(ClientEvents.SESSION_STARTED, {
      startedAt,
      referrer: document.referrer,
      language: deviceLanguage(navigator) ?? '',
      gclid: this.utmParams.gclid,
      utmSource: this.utmParams.utmSource,
      utmMedium: this.utmParams.utmMedium,
      utmCampaign: this.utmParams.utmCampaign,
      utmTerm: this.utmParams.utmTerm,
      utmContent: this.utmParams.utmContent,
      utmTarget: this.utmParams.utmTarget,
      sessionId: id,
    });
  };

  private enhanceServerTrackingData(data: JSONObj = {}) {
    return omitBy(
      {
        ...data,
        $os: os(navigator.userAgent),
        $browser: browser(navigator.userAgent, navigator.vendor),
        $referrer: document.referrer || '$direct',
        $device: device(navigator.userAgent),
        $current_url: window.location.href,
        $browser_version: browserVersion(navigator.userAgent, navigator.vendor),
        $screen_height: window.screen.height,
        $screen_width: window.screen.width,
        occurred_at: new Date(),
        pagePath: window.location.pathname,
        pageQueryArgs: window.location.search,
        btId: this.browserTrackerId,
        eId: uuid(),
        // Include UTM parameters in all events to ensure they're always available
        // This prevents race conditions where events fire before SESSION_STARTED completes
        utmSource: this.utmParams.utmSource,
        utmMedium: this.utmParams.utmMedium,
        utmCampaign: this.utmParams.utmCampaign,
        utmTerm: this.utmParams.utmTerm,
        utmContent: this.utmParams.utmContent,
        utmTarget: this.utmParams.utmTarget,
        gclid: this.utmParams.gclid,
      },
      (value: unknown) =>
        // We want to filter out empty strings, null, and undefined.
        // Don't filter out falsy values because we still want to include 0.
        value === '' || value == null
    );
  }
}
